/** Public beneficiary landing page — AI campaign tracked link (/i/:token).
 *
 * The beneficiary lands here after tapping the SMS/email link. The URL
 * carries a per-contact AI tool token (least-privilege capability) which we
 * use as `Authorization: Bearer <token>` against the token-scoped
 * `/api/ai/*` endpoints — NOT the logged-in user's JWT, so we use a bare
 * axios instance with the API base (never the SPA host).
 *
 * Flow: load context → choose "start now" (open a live session → redirect
 * into the existing /join page for the device check + room) OR "pick a slot"
 * (book an appointment → we email the link). No auth, no AppLayout.
 */
import axios from "axios";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import { BrandLogo } from "@/components/BrandLogo";
import { LocaleSwitcher } from "@/components/LocaleSwitcher";
import { API_BASE } from "@/lib/api";
import { SUPPORTED_LOCALES } from "@/lib/i18n";

type Context = {
  contact_id: string;
  first_name: string;
  company_name: string;
  estimated_duration_minutes: number;
  preferred_language: string;
  status: string;
  has_appointment: boolean;
  appointment_starts_at: string | null;
};

type Slot = {
  slot_id: string;
  starts_at: string;
  ends_at: string;
  remaining: number;
};

type View = "home" | "slots" | "booked";

export default function AiLandingPage() {
  const { token = "" } = useParams<{ token: string }>();
  const { t, i18n } = useTranslation();

  // Bare instance: API host (never the SPA host) + our own bearer, bypassing
  // the shared interceptor that would clobber Authorization with the user JWT.
  const client = useMemo(
    () =>
      axios.create({
        baseURL: API_BASE,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      }),
    [token],
  );

  const [ctx, setCtx] = useState<Context | null>(null);
  const [loadError, setLoadError] = useState(false);
  const [view, setView] = useState<View>("home");
  const [slots, setSlots] = useState<Slot[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState<string | null>(null);
  const [bookedAt, setBookedAt] = useState<string | null>(null);

  // Load context; switch the UI to the beneficiary's language.
  useEffect(() => {
    let alive = true;
    client
      .get<Context>("/ai/context")
      .then((res) => {
        if (!alive) return;
        setCtx(res.data);
        const lang = (res.data.preferred_language || "").slice(0, 2);
        const supported = SUPPORTED_LOCALES.some((l) => l.code === lang);
        if (lang && supported && lang !== i18n.language) {
          void i18n.changeLanguage(lang);
        }
        if (res.data.has_appointment) setView("booked");
      })
      .catch(() => alive && setLoadError(true));
    return () => {
      alive = false;
    };
  }, [client, i18n]);

  function fmtDateTime(iso: string | null): string {
    if (!iso) return "";
    try {
      return new Date(iso).toLocaleString(i18n.language, {
        weekday: "long",
        day: "numeric",
        month: "long",
        hour: "2-digit",
        minute: "2-digit",
      });
    } catch {
      return iso;
    }
  }

  async function startNow() {
    setBusy(true);
    setActionError(null);
    try {
      const res = await client.post<{ session_id: string; join_url: string }>(
        "/ai/start-now",
      );
      // Hand off to the existing join page (device pre-check + room).
      window.location.assign(res.data.join_url);
    } catch (err) {
      const code =
        axios.isAxiosError(err) &&
        (err.response?.data as { code?: string } | undefined)?.code;
      // No operator to open a live call → steer to booking.
      if (code === "NO_OPERATOR") {
        setActionError(t("aiLanding.noOperator"));
        await loadSlots();
      } else {
        setActionError(t("aiLanding.startError"));
      }
    } finally {
      setBusy(false);
    }
  }

  async function loadSlots() {
    setView("slots");
    if (slots) return;
    try {
      const res = await client.get<{ slots: Slot[] }>(
        "/ai/available-slots?count=6",
      );
      setSlots(res.data.slots);
    } catch {
      setSlots([]);
    }
  }

  async function bookSlot(slot: Slot) {
    setBusy(true);
    setActionError(null);
    try {
      await client.post("/ai/book", { slot_id: slot.slot_id });
      setBookedAt(slot.starts_at);
      setView("booked");
    } catch (err) {
      const code =
        axios.isAxiosError(err) &&
        (err.response?.data as { code?: string } | undefined)?.code;
      setActionError(
        code === "SLOT_UNAVAILABLE"
          ? t("aiLanding.slotTaken")
          : t("aiLanding.bookError"),
      );
      // Refresh the list — the taken slot is gone.
      setSlots(null);
      await loadSlots();
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="min-h-dvh bg-nb-bg text-nb-text">
      <div className="mx-auto flex max-w-md flex-col gap-4 px-6 py-10">
        <div className="flex items-center justify-between">
          <BrandLogo />
          <LocaleSwitcher />
        </div>

        {loadError && (
          <div className="card space-y-2">
            <h1 className="text-lg font-semibold">{t("aiLanding.expiredTitle")}</h1>
            <p className="text-sm text-nb-text-2">{t("aiLanding.expiredBody")}</p>
          </div>
        )}

        {!loadError && !ctx && (
          <div className="card">
            <p className="text-sm text-nb-text-2">{t("common.loading")}</p>
          </div>
        )}

        {ctx && !loadError && (
          <div className="card space-y-4">
            <div className="space-y-1">
              <p className="eyebrow">{ctx.company_name}</p>
              <h1 className="text-xl font-semibold">
                {t("aiLanding.greeting", { name: ctx.first_name })}
              </h1>
              <p className="text-sm text-nb-text-2">
                {t("aiLanding.intro", {
                  company: ctx.company_name,
                  minutes: ctx.estimated_duration_minutes,
                })}
              </p>
            </div>

            {actionError && (
              <p className="rounded-md border border-cvp-warn/40 bg-cvp-warn/10 px-3 py-2 text-sm text-cvp-warn">
                {actionError}
              </p>
            )}

            {view === "booked" && (
              <div className="space-y-2">
                <p className="rounded-md border border-cvp-ok/40 bg-cvp-ok/10 px-3 py-2 text-sm text-cvp-ok">
                  {t("aiLanding.bookedConfirm", {
                    when: fmtDateTime(bookedAt ?? ctx.appointment_starts_at),
                  })}
                </p>
                <p className="text-xs text-nb-text-3">
                  {t("aiLanding.bookedHint")}
                </p>
              </div>
            )}

            {view === "home" && (
              <div className="space-y-3">
                <button
                  type="button"
                  className="btn-primary w-full"
                  disabled={busy}
                  onClick={startNow}
                >
                  {busy ? t("aiLanding.starting") : t("aiLanding.startNow")}
                </button>
                <button
                  type="button"
                  className="btn-ghost w-full"
                  disabled={busy}
                  onClick={loadSlots}
                >
                  {t("aiLanding.pickSlot")}
                </button>
                <p className="text-center text-xs text-nb-text-3">
                  {t("aiLanding.noInstall")}
                </p>
              </div>
            )}

            {view === "slots" && (
              <div className="space-y-3">
                <h2 className="text-sm font-semibold">
                  {t("aiLanding.chooseSlot")}
                </h2>
                {slots === null && (
                  <p className="text-sm text-nb-text-2">{t("common.loading")}</p>
                )}
                {slots !== null && slots.length === 0 && (
                  <p className="text-sm text-nb-text-2">
                    {t("aiLanding.noSlots")}
                  </p>
                )}
                <ul className="space-y-2">
                  {(slots ?? []).map((s) => (
                    <li key={s.slot_id}>
                      <button
                        type="button"
                        className="btn-ghost w-full justify-between text-left"
                        disabled={busy}
                        onClick={() => bookSlot(s)}
                      >
                        {fmtDateTime(s.starts_at)}
                      </button>
                    </li>
                  ))}
                </ul>
                <button
                  type="button"
                  className="text-xs text-nb-text-3 hover:underline"
                  onClick={() => setView("home")}
                >
                  ← {t("common.back")}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
